/* Bold Layout — magazine-energy chrome for the Bold template.

   Identity vs Editorial:
   • Editorial = always-visible serif nav, gold accents, soft cream chrome.
   • Bold      = ultra-minimal sticky bar with hamburger-driven side overlay,
                 oversized uppercase nav inside a full-bleed dark overlay,
                 sharp dividers, tight tracking, magazine-cover footer with
                 a giant studio mark and a single line of contact info.

   The Layout intentionally hands almost all vertical real-estate back to the
   page so Bold's type-led hero sections breathe. */

import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { X, Menu, ArrowUpRight } from "lucide-react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useTenantTheme, getSeason } from "@/hooks/useTenantTheme";
import { tenantHref } from "@/lib/tenant";
import TenantSignature from "@/components/tenant/TenantSignature";
import LumetierFooterCredit from "@/components/tenant/LumetierFooterCredit";
import IssueSeal from "@/components/editorial/IssueSeal";
import TenantWordmark from "@/components/tenant/TenantWordmark";

const NAV = [
  { to: "/", label: "Index" },
  { to: "/portfolio", label: "Work" },
  { to: "/services", label: "Services" },
  { to: "/booking", label: "Book" },
  { to: "/contact", label: "Contact" },
];

export default function BoldLayout() {
  useTenantTheme();
  const { settings } = useSiteSettings();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const studioName = settings?.studio_name || "Studio";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close overlay on every route change so the menu never "stays open"
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Lock body scroll while the side overlay is up
  useEffect(() => {
    document.documentElement.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.documentElement.style.overflow = ""; };
  }, [menuOpen]);

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100 antialiased selection:bg-[hsl(41_47%_68%/0.35)] selection:text-stone-50">
      {/* ── Sticky thin top bar ────────────────────────────────────────── */}
      <header
        className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
          scrolled ? "bg-stone-950/85 backdrop-blur-xl border-b border-white/[0.06]" : "bg-transparent"
        }`}
        data-testid="bold-topbar"
      >
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link
            to={tenantHref("/")}
            className="text-[10px] uppercase tracking-[0.45em] text-stone-100 font-medium"
            data-testid="bold-wordmark"
          >
            <TenantWordmark logoUrl={settings?.logo_url} coverLogoUrl={settings?.cover_logo_url} tone="dark" studioName={studioName} imgClassName="h-7 w-auto">
              {studioName}
            </TenantWordmark>
          </Link>
          {/* Index counter — magazine-style "01 / 05" location indicator */}
          <BoldLocationCounter />
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-stone-300 hover:text-stone-50 transition-colors"
            data-testid="bold-menu-trigger"
            aria-label="Open menu"
          >
            <span className="hidden sm:inline">Menu</span>
            <Menu className="w-4 h-4" strokeWidth={1.6} />
          </button>
        </div>
      </header>

      {/* ── Side overlay menu — full-bleed dark sheet from the right ──── */}
      <div
        className={`fixed inset-0 z-50 transition-opacity duration-500 ${
          menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden={!menuOpen}
        data-testid="bold-menu-overlay"
      >
        <div
          className="absolute inset-0 bg-stone-950/95 backdrop-blur-xl"
          onClick={() => setMenuOpen(false)}
        />
        <div
          className={`absolute top-0 right-0 h-full w-full sm:w-[480px] bg-stone-950 border-l border-white/[0.06] flex flex-col transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            menuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="h-16 px-6 lg:px-10 flex items-center justify-between border-b border-white/[0.06]">
            <span className="text-[10px] uppercase tracking-[0.45em] text-stone-500">
              {studioName}
            </span>
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              className="w-9 h-9 inline-flex items-center justify-center text-stone-400 hover:text-stone-50 transition-colors"
              aria-label="Close menu"
              data-testid="bold-menu-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <nav className="flex-1 px-6 lg:px-10 py-12 flex flex-col gap-2 overflow-y-auto">
            {NAV.map((item, i) => (
              <NavLink
                key={item.to}
                to={tenantHref(item.to)}
                end={item.to === "/"}
                data-testid={`bold-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `group flex items-baseline justify-between gap-6 py-3 border-b border-white/[0.06] hover:border-[hsl(41_47%_68%/0.5)] transition-colors ${
                    isActive ? "text-stone-50" : "text-stone-300"
                  }`
                }
              >
                <span className="flex items-baseline gap-5">
                  <span className="text-[10px] tabular-nums text-stone-500 mt-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-serif text-3xl sm:text-4xl font-light uppercase tracking-tight group-hover:translate-x-1 transition-transform duration-300">
                    {item.label}
                  </span>
                </span>
                <ArrowUpRight className="w-4 h-4 text-stone-500 group-hover:text-[hsl(41_47%_68%)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" strokeWidth={1.5} />
              </NavLink>
            ))}
          </nav>
          <div className="px-6 lg:px-10 py-6 border-t border-white/[0.06] text-[10px] uppercase tracking-[0.32em] text-stone-500 flex items-center justify-between gap-4">
            <span>{settings?.contact_email || "—"}</span>
            <span>{settings?.contact_location || "—"}</span>
          </div>
        </div>
      </div>

      {/* ── Page body ──────────────────────────────────────────────────── */}
      <main className="pt-16">
        <Outlet />
      </main>

      {/* ── Footer — magazine cover style ──────────────────────────────── */}
      <BoldFooter studioName={studioName} settings={settings} />

      {/* Issue seal — pinned upper-right under masthead, dark tone */}
      <div className="hidden lg:block fixed top-24 right-6 z-20 pointer-events-none opacity-70" aria-hidden>
        <IssueSeal issue="01" season={getSeason()} year={new Date().getFullYear()} size={88} tone="cream" />
      </div>
      <TenantSignature tone="dark" />
    </div>
  );
}

function BoldLocationCounter() {
  const location = useLocation();
  const idx = NAV.findIndex((n) => {
    const path = location.pathname;
    const target = n.to === "/" ? "" : n.to;
    // Match either /s/<slug>(/<target>)? or /<target> (for non-tenant mounts)
    return path.endsWith(target) || (target === "" && (path.endsWith("/") || path.match(/\/s\/[^/]+$/)));
  });
  const current = idx >= 0 ? idx : 0;
  return (
    <div className="hidden sm:flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-stone-500 tabular-nums">
      <span className="text-stone-300">{String(current + 1).padStart(2, "0")}</span>
      <span className="w-6 h-px bg-stone-700" />
      <span>{String(NAV.length).padStart(2, "0")}</span>
    </div>
  );
}

function BoldFooter({ studioName, settings }) {
  return (
    <footer className="relative border-t border-white/[0.06] mt-32 lg:mt-44" data-testid="bold-footer">
      <div className="max-w-[1480px] mx-auto px-6 lg:px-10">
        {/* Massive studio mark — fills the footer like a magazine cover */}
        <div className="py-20 lg:py-28">
          <div className="text-[10px] uppercase tracking-[0.45em] text-stone-500 mb-8">
            Until next issue
          </div>
          <Link
            to={tenantHref("/booking")}
            className="group block"
            data-testid="bold-footer-book"
          >
            <h2 className="font-serif text-[18vw] sm:text-[16vw] lg:text-[15vw] leading-[0.85] font-light uppercase tracking-[-0.02em] text-stone-100 group-hover:text-[hsl(41_47%_68%)] transition-colors duration-500">
              Book.
            </h2>
          </Link>
        </div>

        {/* Contact grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 py-12 border-t border-white/[0.06]">
          <FooterCol label="Studio" lines={[studioName]} />
          <FooterCol label="Email" lines={[settings?.contact_email || "—"]} />
          <FooterCol label="Phone" lines={[settings?.contact_phone || "—"]} />
          <FooterCol label="Where" lines={[settings?.contact_location || "—"]} />
        </div>

        {/* Bottom row */}
        <div className="py-6 border-t border-white/[0.06] flex flex-wrap items-center justify-between gap-4 text-[10px] uppercase tracking-[0.32em] text-stone-500">
          <span>&copy; {new Date().getFullYear()} {studioName}. All rights reserved.</span>
          <div className="flex items-center gap-6">
            <LumetierFooterCredit className="hover:text-stone-300" testid="bold-lumetier-credit" />
            <Link to="/admin/login" className="hover:text-stone-300 transition-colors" data-testid="bold-footer-admin">
              Studio Manager
            </Link>
            <span>Issue {new Date().getFullYear()}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ label, lines }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.32em] text-stone-500 mb-3">{label}</div>
      {lines.map((l) => (
        <div key={l} className="text-sm text-stone-200">{l}</div>
      ))}
    </div>
  );
}
